import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search as SearchIcon, ArrowLeft, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import SmoothScroll from "@/components/site/SmoothScroll";
import Header from "@/components/site/Header";
import TopMarquee from "@/components/site/TopMarquee";
import Footer from "@/components/site/Footer";
import CategoryNav from "@/components/site/CategoryNav";

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const q0 = params.get("q") || "";
  const [q, setQ] = useState(q0);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = q0 ? `${q0} — खोज | देनवा पोस्ट` : "खोज | देनवा पोस्ट";
    if (!q0 || q0.length < 2) { setData({ q: q0, total: 0, items: [] }); return; }
    setBusy(true);
    api.get(`/public/search?q=${encodeURIComponent(q0)}&limit=40`)
      .then((r) => setData(r.data))
      .catch(() => setData({ q: q0, total: 0, items: [] }))
      .finally(() => setBusy(false));
  }, [q0]);

  const submit = (e) => {
    e.preventDefault();
    const t = q.trim();
    if (t.length < 2) return;
    setParams({ q: t });
  };

  return (
    <SmoothScroll>
      <div className="App font-sans-dev" data-testid="search-page">
        <TopMarquee />
        <Header />
        <CategoryNav />

        <main className="bg-[#F4F4F0] min-h-[70vh]">
          <section className="border-b border-[#0F0F0F] px-4 md:px-8 py-10 md:py-16">
            <div className="max-w-[1000px] mx-auto">
              <Link to="/" className="text-[12px] uppercase tracking-[0.22em] text-[#4A4A4A] hover:text-[#D92525] inline-flex items-center gap-2">
                <ArrowLeft size={14} /> होम
              </Link>
              <div className="mt-5 eyebrow text-[#D92525]">Search · खोज</div>
              <h1 className="mt-3 font-serif-dev font-black text-[36px] md:text-[54px] leading-[1.02]">
                देनवा में <span className="italic">खोजें</span>
              </h1>

              <form onSubmit={submit} className="mt-8 border border-[#0F0F0F] bg-white flex" data-testid="search-form">
                <div className="pl-4 pr-2 flex items-center text-[#4A4A4A]"><SearchIcon size={18} /></div>
                <input
                  data-testid="search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  autoFocus
                  placeholder="खबर, विषय, नेता… हिंदी या English"
                  className="flex-1 outline-none py-3 md:py-4 text-[16px] md:text-[18px] font-sans-dev"
                />
                <button type="submit" data-testid="search-submit"
                  className="bg-[#D92525] text-white px-6 md:px-8 font-sans-dev text-[12px] md:text-[13px] uppercase tracking-[0.18em] hover:bg-[#0A0F1E] transition-colors">
                  खोजें
                </button>
              </form>
              {q0 && data && (
                <div className="mt-4 text-[12px] uppercase tracking-[0.2em] text-[#4A4A4A]" data-testid="search-count">
                  "{q0}" के लिए {data.total} परिणाम
                </div>
              )}
            </div>
          </section>

          <section className="px-4 md:px-8 py-10 md:py-16">
            <div className="max-w-[1000px] mx-auto">
              {busy ? (
                <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-[#D92525]" size={32} /></div>
              ) : !q0 ? (
                <div className="py-16 text-center text-[#4A4A4A]">ऊपर टाइप करके खोज शुरू करें।</div>
              ) : data && data.items.length === 0 ? (
                <div className="py-16 text-center text-[#4A4A4A]">कोई ख़बर नहीं मिली। दूसरे शब्द आज़माएँ।</div>
              ) : (
                <ul className="divide-y divide-[#0F0F0F]/15">
                  {data?.items.map((n, i) => (
                    <motion.li
                      key={n.id}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.35, delay: i * 0.03 }}
                    >
                      <a href={`/news/${n.slug}`} data-testid={`search-result-${i}`}
                        className="flex gap-5 py-5 group">
                        <div className="w-28 sm:w-40 aspect-[4/3] bg-[#0F0F0F]/6 border border-[#0F0F0F]/15 shrink-0 overflow-hidden">
                          {n.img && <img src={n.img} alt={n.title} className="w-full h-full object-contain" loading="lazy" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="eyebrow text-[#D92525] mb-1.5">{n.kicker || n.category}</div>
                          <h3 className="font-serif-dev font-bold text-[17px] md:text-[20px] leading-snug headline-link">{n.title}</h3>
                          {n.summary && <p className="mt-2 font-sans-dev text-[13px] md:text-[14px] text-[#4A4A4A] leading-relaxed line-clamp-2">{n.summary}</p>}
                        </div>
                      </a>
                    </motion.li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </SmoothScroll>
  );
}
